import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { AutomationWizardStepProps } from "../../../AutomationsUtils";
import { LS_AUTOMATION_FILTER } from "../../../../Findings/FindingsFilters/FilterUtils";
import PageWizardFormWrapper from "../../../../Forms/FormWizard/FormWizardFormWrapper";
import PageFormWidget from "../../../../Forms/FormWizard/PageFormRenderer/PageFormWidget/PageFormWidget";
import PageFormButtons from "../../../../Forms/FormWizard/PageFormRenderer/PageFormButtons/PageFormButtons";
import FilterProvider from "../../../../AppView/FilterManagement/FilterProvider/FilterProvider";
import { filterEntityEnum } from "../../../../AppView/FilterManagement/filterEntity";
import ScopeProvider from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import FilterAndScopeInputs from "../../FilterAndScopeInputs/FilterAndScopeInputs";
import {
  TextInput,
  formatNumber,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import "./FindingsStep.scss";

export const MAX_QUEUE_SIZE = 20000;

const FindingsStep: React.FC<AutomationWizardStepProps> = ({
  defaultValues,
  onValidate = () => {},
  isSummary = false,
}) => {
  const methods = useForm({
    mode: "all",
    values: defaultValues,
  });
  const { handleSubmit, control, getValues } = methods;
  const filter = useWatch({ control, name: "filter" });
  const scope = useWatch({ control, name: "scope" });

  return (
    <FormProvider {...methods}>
      <PageWizardFormWrapper
        handleSubmit={handleSubmit}
        onSubmit={onValidate}
        getValues={getValues}
      >
        <PageFormWidget isSummary={isSummary}>
          <div
            className={`findings-step-container ${isSummary ? "is-summary" : ""}`}
          >
            <FilterProvider
              lsFilterKey={LS_AUTOMATION_FILTER}
              filtersEntity={filterEntityEnum.REMEDIATION_ITEM}
            >
              <ScopeProvider>
                <FilterAndScopeInputs isSummary={isSummary} />
              </ScopeProvider>
            </FilterProvider>
            <Controller
              name="name"
              control={control}
              rules={{ required: REQUIRED_ERROR_MESSAGE }}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { error },
              }) => {
                return (
                  <TextInput
                    label="Name"
                    viewMode={isSummary}
                    labelTop
                    required
                    data-testid="name-input"
                    name="name"
                    placeholder="Queue name"
                    error={error}
                    onChange={onChange}
                    value={value}
                    onBlur={onBlur}
                  />
                );
              }}
            />
            <div className={`${!isSummary ? "queue-size-wrapper" : ""}`}>
              {!isSummary && (
                <>
                  <h4>Queue Size</h4>
                  <div className="queue-size-description">
                    {`Defines the maximum number of open tickets this queue will hold at any given time.\nWhen tickets are closed, new ones will open automatically in the next sync to fill the available capacity.`}
                  </div>
                </>
              )}

              <Controller
                name={`queueSize`}
                render={({
                  field: { value, onChange, onBlur },
                  fieldState: { error },
                }) => {
                  return (
                    <TextInput
                      data-testid={"queue-size"}
                      labelTop
                      onChange={onChange}
                      name={"queue-size"}
                      onBlur={onBlur}
                      value={value}
                      label={isSummary ? "Queue Size" : undefined}
                      error={error}
                      min={1}
                      type={"number"}
                      placeholder={`Enter a value between 1 and ${formatNumber(MAX_QUEUE_SIZE)}`}
                      required
                      viewMode={isSummary}
                    />
                  );
                }}
                control={control}
                rules={{
                  required: REQUIRED_ERROR_MESSAGE,
                  pattern: {
                    value: /^[1-9][0-9]*$/,
                    message: "Invalid Queue Size",
                  },
                  max: {
                    value: MAX_QUEUE_SIZE,
                    message: `Queue size cannot be greater than ${formatNumber(MAX_QUEUE_SIZE)}.`,
                  },
                }}
              />
            </div>
          </div>
        </PageFormWidget>
        {!isSummary && (
          <PageFormButtons
            data-testid={"ticketing-queue-form"}
            nextButtonDisabled={!filter || !scope}
          />
        )}
      </PageWizardFormWrapper>
    </FormProvider>
  );
};

export default FindingsStep;
